import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { EventNote, Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useGetComsMonthlyReportRecordsQuery,
  useBackfillComsMonthlyReportRecordsMutation,
} from "../../redux/comsApi";

const COMS_NAVY = "#123456";

const STATUS_LABEL: Record<string, string> = {
  "awaiting-site-qs": "Awaiting QS",
  "awaiting-site-manager": "Awaiting Site Manager",
  approved: "Approved",
};

const STATUS_COLOR: Record<
  string,
  "default" | "warning" | "info" | "success" | "error"
> = {
  "awaiting-site-qs": "warning",
  "awaiting-site-manager": "warning",
  approved: "success",
};

const fmt = (v?: number) =>
  `$${(v ?? 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface Props {
  blockId: string;
}

const MonthlyReportsList: React.FC<Props> = ({ blockId }) => {
  const navigate = useNavigate();
  const {
    data: records = [],
    isLoading,
    refetch,
  } = useGetComsMonthlyReportRecordsQuery(blockId);
  const [backfill, { isLoading: isBackfilling }] =
    useBackfillComsMonthlyReportRecordsMutation();
  const [backfillResult, setBackfillResult] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const dedupedRecords = useMemo(() => {
    const byMonth = new Map<string, (typeof records)[number]>();
    for (const r of records) {
      const key = (r.monthStart ?? "").slice(0, 7);
      if (!byMonth.has(key)) byMonth.set(key, r);
    }
    return [...byMonth.values()];
  }, [records]);

  // Auto-backfill on mount to populate all historical months (A1)
  useEffect(() => {
    if (isLoading) return;
    backfill({ blockId })
      .unwrap()
      .catch(() => {});
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackfill = async () => {
    setConfirmOpen(false);
    try {
      const result = await backfill({ blockId }).unwrap();
      setBackfillResult(
        `Regenerated ${result.generated} month${
          result.generated !== 1 ? "s" : ""
        }. All approval statuses have been reset.`,
      );
      refetch();
    } catch {
      setBackfillResult("Error regenerating reports. Please try again.");
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" sx={{ color: COMS_NAVY, fontWeight: 700 }}>
          Auto-Generated Monthly Reports
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            isBackfilling ? <CircularProgress size={14} /> : <Refresh />
          }
          disabled={isBackfilling}
          onClick={() => setConfirmOpen(true)}
          sx={{
            borderColor: COMS_NAVY,
            color: COMS_NAVY,
            "&:hover": { bgcolor: "#f0f4f8" },
          }}
        >
          Regenerate All Months
        </Button>
      </Box>

      {backfillResult && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setBackfillResult(null)}
        >
          {backfillResult}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Regenerate All Monthly Reports?</DialogTitle>
        <DialogContent>
          This will delete and recreate all monthly reports from the current
          daily report data. Any previously approved reports will have their
          approval status reset.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBackfill}
            sx={{ bgcolor: COMS_NAVY }}
          >
            Regenerate All
          </Button>
        </DialogActions>
      </Dialog>

      {isLoading || isBackfilling ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : dedupedRecords.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, mb: 4 }}>
          <EventNote sx={{ fontSize: 40, color: "#bdbdbd", mb: 1 }} />
          <Typography color="text.secondary">
            No monthly reports yet. Click "Regenerate All Months" to generate
            from daily report history.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: "hidden", mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: COMS_NAVY }}>
                {[
                  "Month",
                  "Reports",
                  "Revenue",
                  "Cost",
                  "Profit / Loss",
                  "Status",
                  "",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ color: "#fff", fontWeight: 600, py: 1.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dedupedRecords.map((r) => {
                const pl = r.periodProfitLoss ?? 0;
                return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(
                        `/coms/daily-site-report/blocks/${blockId}/monthly-report/${r.id}`,
                      )
                    }
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {r.monthLabel ?? `${r.monthStart} → ${r.monthEnd}`}
                    </TableCell>
                    <TableCell>{r.reportsCount ?? 0}</TableCell>
                    <TableCell>{fmt(r.periodRevenue)}</TableCell>
                    <TableCell>
                      {fmt(
                        (r.periodMaterials ?? 0) +
                          (r.periodLabour ?? 0) +
                          (r.periodEquipment ?? 0),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: pl >= 0 ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {fmt(pl)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL[r.status ?? ""] ?? r.status}
                        color={STATUS_COLOR[r.status ?? ""] ?? "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: COMS_NAVY, fontWeight: 500 }}>
                      View →
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default MonthlyReportsList;
